import PropTypes from 'prop-types'
import React, {memo, useEffect} from 'react'
import {equals} from 'ramda'
import {
  ButtonPrimary,
  LogoutIcon,
  CustomerPageLayout,
  HomeIcon,
  GlobeEuropeIcon,
  BillingIcon,
  AddIcon,
  GlobeIcon,
  Row,
  Col,
} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {clientConfig} from '@modifi/client-config'
import {AnnouncementBanner} from '@modifi/plugin-banners'

import {SkeletonLine, SkeletonButton} from '../../ui/skeleton-loaders'
import {
  NAVIGATION_BILLING,
  NAVIGATION_ONBOARDING,
  NAVIGATION_ORDERS,
  NAVIGATION_OVERVIEW,
  NAVIGATION_REPORTS,
  NAVIGATION_SHIPMENT_TRACKER,
} from '../../constants/navigation'
import {asMainNavigationSubject, useAbility} from '../../constants/permissions'
import {CsvFileIcon, CompanyIcon} from '../../ui/icons'

import {ContentContainer} from './styles'

const PageLayout = ({
  children,
  isLoading,
  firstName,
  lastName,
  companyName,
  hasCreatedTrades,
  isOrderCreationDisabled,
  userActions,
  isReportsEnabled,
  fetchAnnouncementData,
  locale,
  hasAnyAnnouncements,
}) => {
  const {t, ready: tReady} = useTranslation('shared', {useSuspense: false})

  const ability = useAbility()

  useEffect(() => {
    fetchAnnouncementData && fetchAnnouncementData(locale, 'customerHub')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAnnouncementData])

  const hasAccessToOverview = ability.can('read', asMainNavigationSubject(NAVIGATION_OVERVIEW))
  const hasAccessToOnboarding = ability.can('read', asMainNavigationSubject(NAVIGATION_ONBOARDING))
  const hasAccessToOrders = ability.can('read', asMainNavigationSubject(NAVIGATION_ORDERS))
  const hasAccessToBilling = ability.can('read', asMainNavigationSubject(NAVIGATION_BILLING))
  const hasAccessToReports = ability.can('read', asMainNavigationSubject(NAVIGATION_REPORTS))
  const hasAccessToShipments = ability.can(
    'read',
    asMainNavigationSubject(NAVIGATION_SHIPMENT_TRACKER)
  )

  const showBilling = hasAccessToBilling && userActions?.canViewBillingPage
  const showReports = hasAccessToReports && isReportsEnabled

  return (
    <CustomerPageLayout
      allowMenuCollapse
      logoItem={{
        to: clientConfig.get('service_hub', '/'),
        isExternal: true,
      }}
      menuItems={[
        clientConfig.get('service_hub', false) !== false &&
          hasAccessToOverview && {
            label: tReady ? t('layout.side-navigation.dashboard.label') : <SkeletonLine />,
            to: clientConfig.get('service_hub'),
            isExternal: true,
            icon: HomeIcon,
            'data-ident': 'dashboard-link',
          },
        clientConfig.get('service_hub', false) !== false &&
          hasAccessToOrders && {
            label: tReady ? t('layout.side-navigation.orders.label') : <SkeletonLine />,
            to: `${clientConfig.get('service_hub')}/${
              userActions?.canCreateOrder ? 'orders' : 'trade-partner-orders'
            }`,
            isExternal: true,
            icon: GlobeEuropeIcon,
            'data-ident': 'orders-link',
          },
        showBilling && {
          label: tReady ? t('layout.side-navigation.billing.label') : <SkeletonLine />,
          to: '/invoices',
          icon: BillingIcon,
          'data-ident': 'billing-link',
        },
        clientConfig.get('service_hub', false) !== false &&
          showReports && {
            label: t('layout.side-navigation.reports.label'),
            to: `${clientConfig.get('service_hub')}/${
              hasCreatedTrades ? 'payout-report' : 'repayment-report'
            }`,
            icon: CsvFileIcon,
            'data-ident': 'reports-link',
          },
        clientConfig.get('service_shipments', false) !== false && hasAccessToShipments
          ? {
              label: tReady ? t('layout.side-navigation.services.label') : <SkeletonLine />,
              to: clientConfig.get('service_shipments'),
              isExternal: true,
              icon: GlobeIcon,
              'data-ident': 'services-link',
              subMenus: [
                {
                  label: tReady ? t('layout.side-navigation.shipments.label') : <SkeletonLine />,
                  to: clientConfig.get('service_shipments'),
                  isExternal: true,
                  'data-ident': 'shipment-services-link',
                },
              ],
            }
          : null,
      ].filter(Boolean)}
      ctaItem={
        userActions?.canCreateOrder && clientConfig.get('service_hub', false) !== false
          ? {
              label: tReady ? (
                <ButtonPrimary big data-target="create-order" active={!isOrderCreationDisabled}>
                  {t('layout.top-navigation.cta-create-order')}
                </ButtonPrimary>
              ) : (
                <SkeletonButton>Finance Trade</SkeletonButton>
              ),
              to: `${clientConfig.get('service_hub')}/create-order`,
              hasButton: true,
              isExternal: true,
              isDisabled: isOrderCreationDisabled,
            }
          : null
      }
      linkItem={
        userActions?.canCreateOrder &&
        !isOrderCreationDisabled &&
        clientConfig.get('service_hub', false) !== false
          ? {
              label: tReady ? t('layout.side-navigation.cta-create-order') : <SkeletonLine />,
              to: `${clientConfig.get('service_hub')}/create-order`,
              isExternal: true,
              icon: AddIcon,
            }
          : null
      }
      userDropdown={{
        firstName,
        lastName,
        companyName,
        items: [
          clientConfig.get('service_onboarding', false) !== false && hasAccessToOnboarding
            ? {
                label: tReady ? (
                  t('layout.side-navigation.company-details.label')
                ) : (
                  <SkeletonLine />
                ),
                to: `${clientConfig.get('service_onboarding')}?navigationSource=hub`,
                icon: CompanyIcon,
                'data-ident': 'onboarding-link',
              }
            : null,
          {
            label: tReady ? t('layout.top-navigation.logout.label') : <SkeletonLine />,
            icon: LogoutIcon,
            to: '/logout',
          },
        ].filter(Boolean),
      }}
      disableNotifications
    >
      <ContentContainer>
        {hasAnyAnnouncements && (
          <Row justifyContent="center">
            <Col>
              <AnnouncementBanner />
            </Col>
          </Row>
        )}
        {!isLoading && children}
      </ContentContainer>
    </CustomerPageLayout>
  )
}

PageLayout.propTypes = {
  children: PropTypes.node.isRequired,
  isLoading: PropTypes.bool,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  companyName: PropTypes.string.isRequired,
  hasCreatedTrades: PropTypes.bool,
  isOrderCreationDisabled: PropTypes.bool,
  userActions: PropTypes.shape({
    canCreateOrder: PropTypes.bool,
    canViewBillingPage: PropTypes.bool,
  }),
  isReportsEnabled: PropTypes.bool,
  fetchAnnouncementData: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  hasAnyAnnouncements: PropTypes.bool.isRequired,
}

PageLayout.defaultProps = {
  isLoading: false,
  hasCreatedTrades: false,
  isOrderCreationDisabled: false,
  userActions: null,
  isReportsEnabled: false,
}

export default memo(PageLayout, equals)
